import React from "react";
import { Image, Text, View } from "@react-pdf/renderer";
import { styles } from "../styles/Slide2Styles";
import shareholders from "../img/shareholders.png";
import externalShareholders from "../img/externalShareholders.png";
import ResultsNavigationBar from "../components/ResultsNavigationBar";
import {
  Company,
  CountryType,
  FoundersType,
  ShareholdersType,
} from "../ResultContext";

export function Slide2({
  image,
  foundersResult,
  companyResult,
  sharesResult,
}: {
  image: string;
  foundersResult: {
    name: string;
    country: CountryType;
    type: FoundersType;
  }[];
  companyResult: Company;
  sharesResult: ShareholdersType;
}) {
  if (!foundersResult || !companyResult || !image || !sharesResult) {
    return <Text>Not all information is filled in for this section</Text>;
  }
  if (foundersResult.length !== sharesResult.shares.length) {
    return (
      <Text>Make sure to update all input fields when adding founders.</Text>
    );
  }
  let externalSharesPercentages = "0";
  const sharesPerFounderPercentage: number[] = [];

  const externalShares = sharesResult.externalShareholders.external
    ? sharesResult.externalShareholders.share
    : "0";
  const sharesPerFounder = sharesResult.shares.map((s) => parseFloat(s.share));

  let sum = sharesPerFounder.reduce((sum, current) => sum + current, 0);

  sum += parseFloat(externalShares);
  sharesPerFounder.map((s) =>
    sharesPerFounderPercentage.push(parseFloat(((s / sum) * 100).toFixed(2)))
  );
  externalSharesPercentages = (
    (parseFloat(externalShares) / sum) *
    100
  ).toFixed(2);

  return (
    <View style={styles.backgroundBlue}>
      <ResultsNavigationBar selectIdx={0} />
      <View style={styles.slideContainer}>
        <View style={styles.companyResults}>
          <View style={styles.companyResultTopTitle}>
            <Text style={styles.textTitle}>CAP TABLE</Text>
          </View>

          <View style={styles.companyResultBottomScheme}>
            {foundersResult.length > 3 ? (
              <View style={styles.schemeTopContainer}>
                {foundersResult.map((founder, founderIndex) => {
                  if (founderIndex > 2) {
                    return (
                      <View style={styles.founderBoxTop} key={founderIndex}>
                        <View style={styles.founderNameContainerTop}>
                          <Text style={styles.textFounderName}>
                            {founder.name}
                          </Text>
                        </View>
                        <View style={styles.founderSharesBox}>
                          <View style={styles.founderSharesBoxLeft}>
                            {founder.type === FoundersType.Company ? (
                              <Image
                                style={styles.companyIcon}
                                src={shareholders}
                              />
                            ) : (
                              <Image
                                style={styles.personIcon}
                                src={shareholders}
                              />
                            )}
                          </View>
                          <View style={styles.founderSharesBoxRight}>
                            <Text style={styles.textShares}>
                              {sharesResult.type === "Percentage"
                                ? `${sharesResult.shares[founderIndex].share}%`
                                : `${sharesPerFounderPercentage[founderIndex]}%`}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.schemeLine} />
                      </View>
                    );
                  }
                })}
              </View>
            ) : null}
            <View style={styles.companyNameBox}>
              <Text>{companyResult.name}</Text>
            </View>
            <View style={styles.schemeBottomContainer}>
              {foundersResult.map((founder, founderIndex) => {
                if (founderIndex < 3) {
                  return (
                    <View style={styles.founderBoxBottom} key={founderIndex}>
                      <View style={styles.schemeLine} />
                      <View style={styles.founderSharesBox}>
                        <View style={styles.founderSharesBoxLeft}>
                          {founder.type === FoundersType.Company ? (
                            <Image
                              style={styles.companyIcon}
                              src={shareholders}
                            />
                          ) : (
                            <Image
                              style={styles.personIcon}
                              src={shareholders}
                            />
                          )}
                        </View>
                        <View style={styles.founderSharesBoxLeft}>
                          <Text style={styles.textShares}>
                            {sharesResult.type === "Percentage"
                              ? `${sharesResult.shares[founderIndex].share}%`
                              : `${sharesPerFounderPercentage[founderIndex]}%`}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.founderNameContainerBottom}>
                        <Text style={styles.textFounderName}>
                          {founder.name}
                        </Text>
                      </View>
                    </View>
                  );
                }
              })}
              {sharesResult.externalShareholders.external ? (
                <View style={styles.founderBoxBottom}>
                  <View style={styles.schemeLineLong} />
                  <View style={styles.founderSharesBox}>
                    <View style={styles.founderSharesBoxLeft}>
                      <Image
                        style={styles.personIcon}
                        src={externalShareholders}
                      />
                    </View>
                    <View style={styles.founderSharesBoxLeft}>
                      <Text style={styles.textShares}>
                        {sharesResult.type === "Percentage"
                          ? `${sharesResult.externalShareholders.share}%`
                          : `${externalSharesPercentages}%`}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.founderNameContainerBottom}>
                    <Text style={styles.textFounderName}>
                      External Shareholders
                    </Text>
                  </View>
                </View>
              ) : null}
            </View>
          </View>
        </View>

        <View style={styles.sharesResults}>
          <View style={styles.companyResultTopTitle}>
            <Text style={styles.textTitle}>
              % of shares of the Founders subject to vesting and call options
            </Text>
          </View>
          <Image style={styles.pieChart} src={image} />
          <View style={styles.sideNoteTextBox}>
            <Text style={styles.textSideNoteTextBox}>
              Only the ‘not earned shares’ are subject to the vesting schedule &
              call options in this co-founder alignment. The ‘earned shares’ are
              not subject to the vesting schedule & call options in this
              co-founder alignment and will stay with the founder regardless of
              a leaver event.
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}
